import type { ThreadKind, ThreadStatus, TicketReportQuery } from '@central/api';

/** The Reports page's URL search: every filter is optional. */
export interface ReportsSearch {
  /** First UTC day, YYYY-MM-DD; default 29 days before `to`. */
  from?: string;
  /** Last UTC day, inclusive; default today. */
  to?: string;
  customerId?: string;
  kind?: ThreadKind;
  status?: ReportStatus;
}

const day = /^\d{4}-\d{2}-\d{2}$/;

/** A YYYY-MM-DD string, or undefined. */
export function isDay(v: unknown): v is string {
  return typeof v === 'string' && day.test(v);
}

export const ticketKinds: Array<{ value: ThreadKind; label: string }> = [
  { value: 'question', label: 'Question' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'license', label: 'License change' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature' },
  { value: 'crash', label: 'Crash report' },
];

/** The ticket types resellers work: never license or renewal. */
export const resellerTicketKinds = ticketKinds.filter(
  (k) => k.value !== 'license' && k.value !== 'renewal',
);

export const kindLabel = Object.fromEntries(ticketKinds.map((k) => [k.value, k.label])) as Record<
  ThreadKind,
  string
>;

export const statusLabel: Record<ThreadStatus, string> = {
  open: 'Open',
  waiting_on_us: 'Waiting on us',
  waiting_on_customer: 'Waiting on customer',
  on_hold: 'On hold',
  closed: 'Resolved',
};

/** The status filter's choices: `active` is every status but closed. */
export const reportStatuses = {
  active: { label: 'Not resolved', q: 'open,waiting_on_us,waiting_on_customer,on_hold' },
  open: { label: 'Open', q: 'open' },
  waiting_on_us: { label: 'Waiting on us', q: 'waiting_on_us' },
  waiting_on_customer: { label: 'Waiting on customer', q: 'waiting_on_customer' },
  on_hold: { label: 'On hold', q: 'on_hold' },
  closed: { label: 'Resolved', q: 'closed' },
} as const;

export type ReportStatus = keyof typeof reportStatuses;

export function isReportStatus(v: unknown): v is ReportStatus {
  return typeof v === 'string' && v in reportStatuses;
}

export function isTicketKind(v: unknown): v is ThreadKind {
  return ticketKinds.some((k) => k.value === v);
}

/** The UTC day of t, YYYY-MM-DD. */
export function utcDay(t: Date): string {
  return t.toISOString().slice(0, 10);
}

/** The default range: the 30 UTC days ending today. */
export function defaultRange(now = new Date()): { from: string; to: string } {
  return { from: utcDay(new Date(now.getTime() - 29 * 86400_000)), to: utcDay(now) };
}

/** The GET /reports/tickets query for a search, with the default range filled in. */
export function reportQuery(s: ReportsSearch, now = new Date()): TicketReportQuery {
  const d = defaultRange(now);
  return {
    from: s.from ?? d.from,
    to: s.to ?? d.to,
    customerId: s.customerId || undefined,
    kind: s.kind || undefined,
    status: s.status ? reportStatuses[s.status].q : undefined,
  };
}

/** An elapsed time in seconds to the minute: "2 d 4 h", "3 h 20 min", "under a minute". */
export function formatElapsed(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const m = Math.floor(seconds / 60);
  const days = Math.floor(m / 1440);
  const hours = Math.floor(m / 60) % 24;
  const mins = m % 60;
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${mins} min`;
  return `${mins} min`;
}

/** Seconds between two RFC 3339 times, or undefined without the end. */
export function secondsBetween(from: string, to?: string): number | undefined {
  return to === undefined ? undefined : (Date.parse(to) - Date.parse(from)) / 1000;
}
